import RefreshButton from '@casops/components/inputs/refresh-button/RefreshButton';
import AssetIdentifier from '@casops/routes/assets/assets/asset/components/asset-identifier/AssetIdentifier';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import {
  AssetDeviceType,
  RegisteredAssetDetailModel,
  useGetAssetQuery,
  useDeleteAssetMutation,
} from '@casops/store/apis/registeredAssetsApi';
import { TagDescription } from '@reduxjs/toolkit/query';
import { ReactElement, ReactNode, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './AssetHeader.module.scss';
import DeleteAction from '@casops/components/tables/cells/model-actions/components/delete-action/DeleteAction';
import DeleteDialogButton from '@casops/dialogs/delete-model-dialog/components/button/DeleteDialogButton';
import EditItemButton from '@casops/components/inputs/edit-item-button/EditItemButton';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';
import { useTranslation } from 'react-i18next';
interface AssetHeaderProps {
  modelId?: number;
  asset: RegisteredAssetDetailModel | undefined;
  requestRemainder: Omit<ReturnType<typeof useGetAssetQuery>, 'data'>;
  vehicleGroupChips: ReactNode;
}

type Model = RegisteredAssetDetailModel;

const modelName = ModelName.Asset;
const sidePanel = SidePanel.ManageAsset;
const permission = Permission.AssetManagement;

const getItemName = (data: Model): string => data.n;

export const AssetHeader = ({
  modelId,
  asset,
  requestRemainder,
  vehicleGroupChips,
}: AssetHeaderProps): ReactElement => {
  const { t } = useTranslation();

  const tagsToClear = useMemo<TagDescription<QueryTag>[]>(
    () => [
      { type: QueryTag.AssetRegistered, id: modelId },
      { type: QueryTag.Geofence, id: TagList },
      { type: QueryTag.GeofenceArea, id: TagList },
      { type: QueryTag.DeviceMessage, id: modelId },
      { type: QueryTag.AssetGroup, id: TagList },
      { type: QueryTag.ConfigurationGroup, id: TagList },
    ],
    [modelId],
  );

  const showActions = useIsUserWithPermission(permission);
  const disableActions = requestRemainder.isError || typeof asset === 'undefined';

  return (
    <header className={styles.header}>
      {asset ? (
        <>
          <AssetIdentifier
            name={asset.n}
            mismatch={asset.stN}
            deviceId={asset.did}
            objectType={asset.obj}
            deviceType={asset.hw ?? AssetDeviceType.Unknown}
            vehicleGroupChips={vehicleGroupChips}
            displayStatus={asset.stD}
            showIcon
          />
          <div className={styles.actions}>
            {showActions && (
              <>
                <EditItemButton
                  modelId={modelId}
                  modelName={modelName}
                  sidePanel={sidePanel}
                  permission={permission}
                  label={t('Edit')}
                  // Disable while loading for consistency with the delete button.
                  disabled={disableActions}
                />

                {!disableActions ? (
                  <DeleteAction<Model>
                    modelName={modelName}
                    data={asset}
                    getItemName={getItemName}
                    getQueryArg={getDefaultModelArg}
                    useDeleteModelMutation={useDeleteAssetMutation}
                    canDeleteItem={!!asset.id}
                    isIconButton={false}
                    navigateTo="/assets/assets"
                  />
                ) : (
                  // Display a function-less disabled button to preserve layout while loading data.
                  <DeleteDialogButton isIconButton={false} disabled={true} />
                )}
              </>
            )}
            <RefreshButton tagsToClear={tagsToClear} />
          </div>
        </>
      ) : (
        <Skeleton width={250} height={32} />
      )}
    </header>
  );
};

export default AssetHeader;
